import React, { useState, useEffect, useRef } from "react";
import { Spinner, Modal, ModalHeader, ModalBody, Button } from "flowbite-react";
import { useAuth } from "../context/AuthContext";
import axios from "../api/axios";
import UserProfileDetailModal from "../modals/userProfileDetailModal";
import * as adminService from "../api/adminService";
import { 
  HiSearch, 
  HiOutlineFilter, 
  HiOutlineRefresh, 
  HiOutlinePencil, 
  HiOutlineTrash, 
  HiX, 
  HiCheck,
  HiOutlineExclamation,
  HiShieldCheck
} from "react-icons/hi";

const UsersList = ({ 
  usersList, 
  currentPage, 
  setCurrentPage, 
  pageSize = 20, 
  setPageSize, 
  totalUsers = 0, 
  totalPages = 0, 
  handleUpdates, 
  highlightedUser, 
  isServerSidePagination = false,
  isPageLoading = false,
  searchText = "",       // controlled search value from parent (server-side mode)
  onSearchChange = null  // callback to notify parent of search changes
}) => {
  const { getToken } = useAuth();

  const effectiveUsersList = Array.isArray(usersList) ? usersList : [];
  const useServerSidePagination = isServerSidePagination;

  // Local input state — drives the text box immediately (no lag)
  // In server-side mode this mirrors the parent's searchText; in local mode it's self-contained.
  const [inputValue, setInputValue] = useState(useServerSidePagination ? searchText : "");

  // Keep local input in sync if parent resets searchText (e.g. Refresh button)
  const prevSearchText = useRef(searchText);
  useEffect(() => {
    if (useServerSidePagination && searchText !== prevSearchText.current) {
      setInputValue(searchText);
      prevSearchText.current = searchText;
    }
  }, [searchText, useServerSidePagination]);

  // For local (non-server-side) mode keep a separate searchTerm state
  const [localSearchTerm, setLocalSearchTerm] = useState("");

  // Debounce: notify parent 400 ms after the user stops typing
  const debounceTimer = useRef(null);
  const handleInputChange = (value) => {
    setInputValue(value);
    if (useServerSidePagination && onSearchChange) {
      clearTimeout(debounceTimer.current);
      if (value === "") {
        // Clear immediately so the full list is fetched right away
        onSearchChange("");
      } else {
        debounceTimer.current = setTimeout(() => {
          onSearchChange(value);
        }, 400);
      }
    } else {
      setLocalSearchTerm(value);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  // Derive the search term used for local filtering
  const searchTerm = useServerSidePagination ? "" : localSearchTerm;

  const [sortField, setSortField] = useState("joiningDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filter and sort users (client-side for non-server-side pagination, or just filter for display)
  const filteredUsers = useServerSidePagination ? effectiveUsersList : effectiveUsersList.filter((user) => {
    // Search filter
    const matchesSearch = 
      !searchTerm ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Role filter
    const matchesRole = 
      roleFilter === "all" || 
      (roleFilter === "admin" && user.accessLevel === 0) ||
      (roleFilter === "crm" && user.accessLevel === 1) ||
      (roleFilter === "api" && user.accessLevel === 2) ||
      (roleFilter === "moderator" && user.accessLevel === 3) ||
      (roleFilter === "user" && (user.accessLevel === 4 || user.accessLevel === undefined));
    
    // Plan filter
    const matchesPlan = 
      statusFilter === "all" || 
      (statusFilter === "trial" && user.plan === 0) ||
      (statusFilter === "developer" && user.plan === 1) ||
      (statusFilter === "enterprise" && user.plan === 2) ||
      (statusFilter === "educational" && (user.educationalAccount || user.plan === 3));
    
    return matchesSearch && matchesRole && matchesPlan;
  });

  const sortedUsers = useServerSidePagination ? filteredUsers : [...filteredUsers].sort((a, b) => {
    let valA, valB;

    // Handle name sorting specifically
    if (sortField === "name") {
      // For name sorting, prioritize the name field but fallback to email
      valA = a.name ? a.name.toLowerCase() : (a.email ? a.email.split('@')[0].toLowerCase() : '');
      valB = b.name ? b.name.toLowerCase() : (b.email ? b.email.split('@')[0].toLowerCase() : '');
    }
    // Handle email sorting with case insensitivity
    else if (sortField === "email") {
      valA = (a.email || "").toLowerCase();
      valB = (b.email || "").toLowerCase();
    }
    // Handle tokens sorting as numbers
    else if (sortField === "tokens") {
      valA = Number(a.tokens || 0);
      valB = Number(b.tokens || 0);
    }
    // Handle role sorting with case insensitivity
    else if (sortField === "role") {
      const getRoleValue = (user) => {
        // Return a value that can be sorted based on accessLevel
        if (user.accessLevel === 0) return "0-SuperAdmin";
        if (user.accessLevel === 1) return "1-CRMAdmin";
        if (user.accessLevel === 2) return "2-APIManager";
        if (user.accessLevel === 3) return "3-SupportCoordinator";
        return "4-User";
      };
      valA = getRoleValue(a);
      valB = getRoleValue(b);
    }
    // Default case: use the field directly
    else {
      valA = a[sortField];
      valB = b[sortField];
      // Convert string values to lowercase for case-insensitive comparison
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
    }

    // Handle null/undefined values
    if (valA === undefined || valA === null) valA = '';
    if (valB === undefined || valB === null) valB = '';

    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Handle sort change
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Pagination
  const itemsPerPage = useServerSidePagination ? pageSize : 10; // Use server pageSize or default to 10
  const calculatedTotalPages = useServerSidePagination ? totalPages : Math.ceil(filteredUsers.length / itemsPerPage);
  const userCount = useServerSidePagination ? totalUsers : filteredUsers.length;
  
  // If current page is greater than total pages, reset to page 1 or last page
  useEffect(() => {
    if (!useServerSidePagination && filteredUsers.length > 0 && currentPage > Math.max(1, calculatedTotalPages)) {
      setCurrentPage(Math.max(1, calculatedTotalPages));
    }
  }, [filteredUsers.length, calculatedTotalPages, currentPage, setCurrentPage, useServerSidePagination]);
  
  const paginatedUsers = useServerSidePagination ? sortedUsers : sortedUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Effect to scroll to highlighted user
  useEffect(() => {
    if (highlightedUser) {
      // Find if the highlighted user is in the current page
      const userIndex = paginatedUsers.findIndex(user => user._id === highlightedUser._id);
      
      if (userIndex === -1) {
        // User not found on this page, find what page they should be on
        const allUserIndex = sortedUsers.findIndex(user => user._id === highlightedUser._id);
        if (allUserIndex !== -1) {
          const targetPage = Math.floor(allUserIndex / itemsPerPage) + 1;
          setCurrentPage(targetPage);
          
          // Also set search term if available
          if (highlightedUser.name || highlightedUser.email) {
            handleInputChange(highlightedUser.name || highlightedUser.email);
          }
        }
      }
    }
  }, [highlightedUser, paginatedUsers, sortedUsers, itemsPerPage, setCurrentPage]);



  // Open user detail modal
  const openUserModal = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (user, e) => {
    e.stopPropagation(); // Prevent opening the user modal
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  // Handle user deletion
  const handleDeleteUser = async () => {
    try {
      setIsLoading(true);
      const token = await getToken();
      await axios.delete(`/users/${selectedUser._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setToastMessage(`User ${selectedUser.name || selectedUser.email} deleted successfully`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
      setIsDeleteModalOpen(false);
      handleUpdates();
    } catch (error) {
      console.error("Failed to delete user:", error);
      setToastMessage("Failed to delete user");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle user active status
  const toggleUserStatus = async (userId, isVerified, e) => {
    e.stopPropagation(); // Prevent opening the user modal
    
    try {
      // Find the user to get their email
      const user = usersList.find(u => u._id === userId);
      if (!user || !user.email) {
        throw new Error("User email not found");
      }

      // Use the new admin service endpoint for blocking/unblocking
      if (isVerified) {
        // If verified, block the user
        await adminService.blockUser(user.email);
        setToastMessage(`User ${user.name || user.email} blocked successfully`);
      } else {
        // If not verified, unblock the user
        await adminService.unblockUser(user.email);
        setToastMessage(`User ${user.name || user.email} unblocked successfully`);
      }
      
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
      handleUpdates();
    } catch (error) {
      console.error("Failed to update user status:", error);
      setToastMessage("Failed to update user status");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
    }
  };

  // Utility function to update user role
  const updateUserRole = async (userId, role, closeModal = true) => {
    try {
      // Find the user to get their email
      const user = usersList.find(u => u._id === userId);
      if (!user || !user.email) {
        throw new Error("User email not found");
      }

      const token = await getToken();
      let accessLevel = 4; // Default regular user
      
      if (role === "admin") {
        accessLevel = 0; // SuperAdmin
      } else if (role === "crm") {
        accessLevel = 1; // CRMAdmin
      } else if (role === "api") {
        accessLevel = 2; // APIManager
      } else if (role === "moderator") {
        accessLevel = 3; // SupportCoordinator
      } else if (role === "user") {
        accessLevel = 4; // Regular User - explicitly set
      } else if (role === "educational") {
        // For educational, we keep the user level but set the educational flag
        await axios.put(
          `/users/${userId}`,
          { educationalAccount: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setToastMessage(`User set to Educational successfully`);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
        if (closeModal) setIsUserModalOpen(false);
        handleUpdates();
        return;
      }
      
      // Use the new admin service endpoint for changing access level
      await adminService.changeUserAccessLevel(user.email, accessLevel);
      
      setToastMessage(`User role updated to ${role} successfully`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
      
      // Don't close the modal automatically
      // if (closeModal) setIsUserModalOpen(false);
      
      // Make sure to update the UI regardless of success or failure
      handleUpdates();
    } catch (error) {
      console.error("Failed to update user role:", error);
      setToastMessage("Failed to update user role");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
      
      // Still call handleUpdates to refresh the UI
      handleUpdates();
    }
  };

  // Get user role styling — colored badge pills
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "SuperAdmin": return "bg-red-500/15 text-red-700 border border-red-500/20";
      case "CRMAdmin": return "bg-orange-500/15 text-orange-700 border border-orange-500/20";
      case "APIManager": return "bg-purple-500/15 text-purple-700 border border-purple-500/20";
      case "SupportCoordinator": return "bg-blue-500/15 text-blue-700 border border-blue-500/20";
      default: return "bg-gray-500/10 text-[#5C6353] border border-gray-500/10";
    }
  };

  // Get user plan styling — colored badge pills
  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case "Trial": return "bg-yellow-500/15 text-yellow-700 border border-yellow-500/20";
      case "Developer": return "bg-green-500/15 text-green-700 border border-green-500/20";
      case "Enterprise": return "bg-indigo-500/15 text-indigo-700 border border-indigo-500/20";
      case "Educational": return "bg-teal-500/15 text-teal-700 border border-teal-500/20";
      default: return "bg-gray-500/10 text-[#8C9086] border border-gray-500/10";
    }
  };

  // Get verification styling — colored badge pills
  const getVerificationBadge = (user) => {
    if (user.verified) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-500/15 text-green-700 border border-green-500/20">
          <HiShieldCheck className="w-3 h-3" />
          Verified
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-700 border border-amber-500/20">
        Pending
      </span>
    );
  };

  // Get user role from accessLevel
  const getUserRole = (user) => {
    if (user.accessLevel === 0) return "SuperAdmin";
    if (user.accessLevel === 1) return "CRMAdmin";
    if (user.accessLevel === 2) return "APIManager";
    if (user.accessLevel === 3) return "SupportCoordinator";
    return "User";
  };

  // Get user plan
  const getUserPlan = (user) => {

    if (typeof user.plan === "number") {
      switch (user.plan) {
        case 0: return "Trial";
        case 1: return "Developer";
        case 2: return "Enterprise";
        case 3: return "Educational";
        default: return "NA";
      }
    }
    
    return "NA";
  };

  // Get user tokens (using the API's tokens field)
  const getUserTokens = (user) => {
    return user.tokens || 0;
  };
  
  // Get user display name and initial
  const getUserInitial = (user) => {
    if (user.name) {
      return user.name.charAt(0);
    }
    return user.email?.charAt(0) || '?';
  };
  
  const getUserDisplayName = (user) => {
    return user.name || user.email?.split('@')[0] || 'Unknown';
  };

  return (
    <div className="h-full">
      <div className="h-full bg-[#232323] rounded-2xl p-6 flex flex-col">
        {/* Top-right controls */}
        <div className="flex items-center justify-end gap-3 mb-5">
          <button
            onClick={() => {
              setInputValue("");
              setLocalSearchTerm("");
              setRoleFilter("all");
              setStatusFilter("all");
              setSortField("joiningDate");
              setSortDirection("desc");
              setCurrentPage(1);
              if (isServerSidePagination && onSearchChange) {
                onSearchChange("");
              }
              if (isServerSidePagination) {
                handleUpdates(true);
              } else {
                handleUpdates();
              }
            }}
            className="p-2 rounded-full bg-[#1E1F21] hover:bg-[#212224] text-gray-200 transition-colors"
            title="Refresh"
          >
            <HiOutlineRefresh className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-full transition-colors ${
              showFilters ? "bg-lime-400 text-gray-900" : "bg-[#1E1F21] hover:bg-[#212224] text-gray-200"
            }`}
            title="Filter"
          >
            <HiOutlineFilter className="w-5 h-5" />
          </button>
          <div className="relative">
            <HiSearch className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search by user"
              className="w-64 pl-10 pr-9 h-10 rounded-full bg-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-lime-400"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
            />
            {inputValue && (
              <button
                onClick={() => handleInputChange("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                title="Clear search"
              >
                <HiX className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-[#1E1F21] rounded-xl shadow-sm p-4 mb-5">
          {useServerSidePagination && (
            <div className="mb-3 p-2 rounded-lg border border-gray-700 bg-[#28292B]">
              <p className="text-sm text-gray-300">
                <strong>Note:</strong> Filters and sorting work on the current page data only. For full filtering across all users, API-level support would be needed.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-[#28292B] text-gray-100 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-lime-400 cursor-pointer"
            >
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="moderator">Support Coordinator</option>
              <option value="admin">Super Admin</option>
              <option value="crm">CRM Admin</option>
              <option value="api">API Manager</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Plan</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-[#28292B] text-gray-100 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-lime-400 cursor-pointer"
            >
              <option value="all">All Plans</option>
              <option value="trial">Trial</option>
              <option value="developer">Developer</option>
              <option value="enterprise">Enterprise</option>
              <option value="educational">Educational</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Sort By</label>
            <select
              value={sortField}
              onChange={(e) => handleSort(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-[#28292B] text-gray-100 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-lime-400 cursor-pointer"
            >
              <option value="joiningDate">Date Created</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="tokens">Tokens</option>
              <option value="role">Role</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Order</label>
            <select
              value={sortDirection}
              onChange={(e) => setSortDirection(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-[#28292B] text-gray-100 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-lime-400 cursor-pointer"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-5xl bg-lime-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200 relative">
          {/* Loading Overlay */}
          {isPageLoading && (
            <div className="absolute inset-0 bg-[#E8ECDA]/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-3">
                <Spinner size="lg" />
                <p className="text-sm text-[#5C6353] font-medium">Loading users...</p>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-200 border-b border-gray-300 text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Verification</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Tokens</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/[0.04]">
              {filteredUsers.length > 0 ? (
                paginatedUsers.length > 0 ? (
                  paginatedUsers.map((user, index) => (
                    <tr 
                      key={user._id} 
                      className={`hover:bg-[#DEE3D0] cursor-pointer transition-colors duration-200 group ${
                        highlightedUser && highlightedUser._id === user._id ? 'border-l-4 border-[#BDE958]' : ''
                      }`}
                      onClick={() => openUserModal(user)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#D5DABF] flex items-center justify-center text-[#5C6353] font-bold text-xs">
                            {user.imageUrl ? (
                              <img
                                src={user.imageUrl}
                                alt={getUserDisplayName(user)}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              getUserInitial(user).toUpperCase()
                            )}
                          </div>
                          <span className="text-sm font-semibold text-[#161616] truncate max-w-[180px]">
                            {getUserDisplayName(user)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${getRoleBadgeColor(getUserRole(user))}`}>
                          {getUserRole(user)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getVerificationBadge(user)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${getPlanBadgeColor(getUserPlan(user))}`}>
                          {getUserPlan(user)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-[#161616] truncate max-w-[200px] block">
                          {user.email}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-bold text-[#161616]">{getUserTokens(user)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); openUserModal(user); }}
                            className="p-1.5 rounded-lg text-[#8C9086] hover:text-[#161616] hover:bg-black/5 transition-colors"
                            title="Edit user"
                          >
                            <HiOutlinePencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => openDeleteModal(user, e)}
                            className="p-1.5 rounded-lg text-[#8C9086] hover:text-red-500 hover:bg-red-500/10 transition-colors"
                            title="Delete user"
                          >
                            <HiOutlineTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="w-16 h-16 bg-[#D5DABF]/30 rounded-full flex items-center justify-center text-[#8C9086]">
                          <HiOutlineExclamation className="w-8 h-8" />
                        </div>
                        <p className="text-[#8C9086] font-medium text-sm">No users on this page</p>
                        <p className="text-[#8C9086] text-xs">Try going to a previous page</p>
                      </div>
                    </td>
                  </tr>
                )
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="w-16 h-16 bg-[#D5DABF]/30 rounded-full flex items-center justify-center text-[#8C9086]">
                        <HiSearch className="w-8 h-8" />
                      </div>
                      <p className="text-[#8C9086] font-medium text-sm">No users found</p>
                      <p className="text-[#8C9086] text-xs">Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {userCount > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-lime-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-semibold text-[#8C9086] rounded-xl hover:bg-[#D5DABF] disabled:opacity-50 disabled:hover:bg-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#8C9086]/50"
                >
                  &larr; Previous
                </button>
              </div>
              
{(() => {
                const getPageNumbers = () => {
                  const pages = [];
                  const totalPageCount = calculatedTotalPages;
                  
                  if (totalPageCount <= 7) {
                    // Show all pages if total is 7 or less
                    for (let i = 1; i <= totalPageCount; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Always show first page
                    pages.push(1);
                    
                    if (currentPage <= 4) {
                      // Show pages 2, 3, 4, 5, ..., last
                      for (let i = 2; i <= 5; i++) {
                        pages.push(i);
                      }
                      if (totalPageCount > 6) {
                        pages.push('...');
                      }
                    } else if (currentPage >= totalPageCount - 3) {
                      // Show 1, ..., last-4, last-3, last-2, last-1, last
                      if (totalPageCount > 6) {
                        pages.push('...');
                      }
                      for (let i = totalPageCount - 4; i <= totalPageCount - 1; i++) {
                        if (i > 1) pages.push(i);
                      }
                } else {
                      // Show 1, ..., current-1, current, current+1, ..., last
                      if (currentPage > 3) {
                        pages.push('...');
                      }
                      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                        if (i > 1 && i < totalPageCount) pages.push(i);
                      }
                      if (currentPage < totalPageCount - 2) {
                        pages.push('...');
                      }
                    }
                    
                    // Always show last page
                    if (totalPageCount > 1) {
                      pages.push(totalPageCount);
                    }
                  }
                  
                  return pages;
                };

                return getPageNumbers().map((pageNum, idx) => {
                  if (pageNum === '...') {
                    return (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-1.5 text-gray-500"
                      >
                        ...
                      </span>
                    );
                }
                
                return (
                  <button
                    key={pageNum}
                      onClick={() => {
                        setCurrentPage(pageNum);
                      }}
                    className={`px-3 py-1.5 rounded-lg transition-colors ${
                      pageNum === currentPage
                        ? "bg-blue-600 text-white"
                        : "text-gray-700 hover:bg-white"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
                });
              })()}

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.min(calculatedTotalPages, currentPage + 1))}
                  disabled={currentPage === calculatedTotalPages}
                  className="px-4 py-2 text-sm font-semibold text-[#8C9086] rounded-xl hover:bg-[#D5DABF] disabled:opacity-50 disabled:hover:bg-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#8C9086]/50"
                >
                  Next &rarr;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* User Details Modal */}
      {selectedUser && (
        <UserProfileDetailModal
          isOpen={isUserModalOpen}
          onClose={() => setIsUserModalOpen(false)}
          selectedUser={selectedUser}
          updateUserRole={updateUserRole}
          toggleUserStatus={toggleUserStatus}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setIsUserModalOpen={setIsUserModalOpen}
          getUserDisplayName={getUserDisplayName}
          getUserInitial={getUserInitial}
          getUserPlan={getUserPlan}
          getUserTokens={getUserTokens}
          setToastMessage={setToastMessage}
          setShowToast={setShowToast}
          handleUpdates={handleUpdates}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={isDeleteModalOpen}
        size="md"
        popup
        onClose={() => setIsDeleteModalOpen(false)}
        theme={{
          root: {
            base: "fixed top-0 right-0 left-0 z-50 h-modal h-screen overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
            show: {
              on: "flex bg-gray-900 bg-opacity-80 dark:bg-opacity-90 backdrop-blur-sm",
              off: "hidden"
            }
          },
          content: {
            base: "relative h-full w-full p-4 md:h-auto",
            inner: "relative rounded-xl bg-white shadow-2xl dark:bg-[#2a2d36] flex flex-col max-h-[90vh] w-full max-w-md mx-auto"
          }
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-red-600"></div>
        <ModalHeader className="px-6 pt-5 pb-0 border-none" theme={{ close: { base: "ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white" } }} />
        <ModalBody className="px-6 py-5">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-600/20 mb-4">
              <HiOutlineExclamation className="h-10 w-10 text-red-500" />
            </div>
            <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
              Confirm User Deletion
            </h3>
            <p className="mb-5 text-gray-600 dark:text-gray-400 text-sm max-w-md mx-auto">
              Are you sure you want to delete{" "}
              {selectedUser && (
                <span className="font-semibold text-gray-900 dark:text-white">
                  {getUserDisplayName(selectedUser)}
                </span>
              )}
              's account? This action cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <Button
                color="failure"
                size="sm"
                pill
                className="bg-red-600 hover:bg-red-700 border-none"
                onClick={handleDeleteUser}
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" className="mr-2" /> : null}
                <span>Yes, delete account</span>
              </Button>
              <Button
                color="gray"
                size="sm"
                pill
                className="bg-gray-600 hover:bg-gray-700 border-none"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Toast notification */}
      {showToast && (
        <div className="fixed bottom-5 right-5 z-[9999]">
          <div className="bg-white dark:bg-[#28292B] rounded-lg shadow-lg p-4 flex items-center space-x-3 animate-fadeIn">
            <div className="flex-shrink-0">
              {toastMessage.startsWith("Failed") || toastMessage.toLowerCase().includes("error") ? (
                <HiOutlineExclamation className="h-5 w-5 text-red-500" />
              ) : (
                <HiCheck className="h-5 w-5 text-green-500" />
              )}
            </div>
            <div className="flex-1 mr-5">
              <p className="text-sm text-gray-900 dark:text-white font-medium">{toastMessage}</p>
            </div>
            <button 
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setShowToast(false)}
            >
              <HiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
